import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import capabilityMatrix from "../../data/apiCapabilityMatrix.json";
import { buildPresignedCurl } from "../../utils/graphqlExportCurl";

function CapabilityTable({ rows, columns }) {
  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col} style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd" }}>
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {Object.values(row).map((val, j) => (
              <td key={j} style={{ padding: "8px", borderBottom: "1px solid #eee", verticalAlign: "top" }}>
                {typeof val === "boolean" ? (val ? "✅" : "—") : val}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function OperationDoc({ op }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} id={`op-${op.name}`}>
      <Typography variant="h6" gutterBottom>
        {op.name}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Chip label={op.kind} size="small" />
        <Chip label={op.domain} size="small" variant="outlined" />
        {op.min_role && <Chip label={`Rol: ${op.min_role}`} size="small" color="primary" variant="outlined" />}
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        {op.description || "Sin descripción."}
      </Typography>
      {op.parameters?.length > 0 && (
        <>
          <Typography variant="subtitle2">Parámetros</Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
            {op.parameters.map((p) => (
              <li key={p.name}>
                <code>{p.name}</code> ({p.type}){p.required ? " *" : ""}
              </li>
            ))}
          </Typography>
        </>
      )}
      <Box
        component="pre"
        sx={{
          mt: 1,
          p: 1.5,
          bgcolor: "grey.50",
          borderRadius: 1,
          fontSize: "0.8rem",
          overflow: "auto",
        }}
      >
        {op.kind === "MUTATION"
          ? `mutation { ${op.name} { … } }`
          : `query { ${op.name} { … } }`}
      </Box>
    </Paper>
  );
}

export default function DevPortalDocs({ operations = [] }) {
  const [section, setSection] = useState(0);
  const [search, setSearch] = useState("");

  const filteredOps = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return operations;
    return operations.filter(
      (op) =>
        op.name.toLowerCase().includes(q) ||
        (op.description || "").toLowerCase().includes(q) ||
        op.domain.toLowerCase().includes(q)
    );
  }, [operations, search]);

  const presignedExample = buildPresignedCurl({
    token: "<access_token from POST /auth>",
    payload: {
      key: "documents/unclassified/mi-archivo.pdf",
      presigned_type: "PUT_OBJECT",
      file_name: "mi-archivo.pdf",
    },
  });

  return (
    <Box>
      <Tabs value={section} onChange={(_, v) => setSection(v)} sx={{ mb: 2 }}>
        <Tab label="Introducción" />
        <Tab label="Guía rápida" />
        <Tab label="Referencia GraphQL" />
        <Tab label="REST" />
        <Tab label="Matriz de capacidades" />
        <Tab label="Errores comunes" />
      </Tabs>

      {section === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Introducción
          </Typography>
          <Typography paragraph>
            Base URL: <code>{process.env.REACT_APP_API_BASE_URL}</code>
          </Typography>
          <Typography paragraph>
            GraphQL: <code>{process.env.REACT_APP_GRAPHQL_URI || "/graphql"}</code>
          </Typography>
          <Typography paragraph>
            Autenticación integraciones M2M (SPEC-25):
          </Typography>
          <Box component="pre" sx={{ fontSize: "0.75rem", overflow: "auto", bgcolor: "grey.50", p: 2, mb: 2 }}>
{`# 1. Obtener token (~60 min)
curl -X POST "${process.env.REACT_APP_API_BASE_URL || "https://api.tagnize.com"}/auth" \\
  -H "Authorization: Basic $(echo -n 'CLIENT_ID:ACCESS_KEY' | base64)"

# 2. Usar Bearer en GraphQL / REST
curl -H "Authorization: Bearer ACCESS_TOKEN" ...`}
          </Box>
          <Typography paragraph>
            Sesión humana: header <code>Authorization: Bearer &lt;JWT Cognito IdToken&gt;</code>
          </Typography>
          <Typography paragraph>
            Rate limits: sujetos a API Gateway (v1 sin cuotas documentadas por tenant).
          </Typography>
          <Typography>
            Upload: idempotencia vía <code>content_md5</code> en presigned; duplicados retornan 409.
          </Typography>
        </Paper>
      )}

      {section === 1 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Guía rápida — subir PDF y consultar estado
          </Typography>
          <Typography component="ol" sx={{ pl: 3 }}>
            {capabilityMatrix.quickstart.map((step, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                {step}
              </li>
            ))}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2">Ejemplo presigned</Typography>
          <Box component="pre" sx={{ fontSize: "0.75rem", overflow: "auto", bgcolor: "grey.50", p: 2 }}>
            {presignedExample}
          </Box>
        </Paper>
      )}

      {section === 2 && (
        <Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar operaciones…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          {filteredOps.map((op) => (
            <OperationDoc key={op.name} op={op} />
          ))}
        </Box>
      )}

      {section === 3 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Referencia REST
          </Typography>
          {capabilityMatrix.restEndpoints.map((ep) => (
            <Box key={ep.path} sx={{ mb: 2 }}>
              <Typography>
                <Chip label={ep.method} size="small" /> <code>{ep.path}</code> — {ep.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rol mínimo: {ep.minRole}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {section === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            ✅ Disponible vía API
          </Typography>
          <CapabilityTable
            columns={["Capacidad", "GraphQL", "REST", "Notas"]}
            rows={capabilityMatrix.available.map((r) => ({
              a: r.capability,
              b: r.graphql,
              c: r.rest,
              d: r.notes,
            }))}
          />
          <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
            ⚠️ Parcial / indirecto
          </Typography>
          <CapabilityTable
            columns={["Capacidad", "Estado", "Alternativa"]}
            rows={capabilityMatrix.partial.map((r) => ({
              a: r.capability,
              b: r.status,
              c: r.alternative,
            }))}
          />
          <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
            ❌ No disponible vía API
          </Typography>
          <CapabilityTable
            columns={["Capacidad", "Motivo"]}
            rows={capabilityMatrix.unavailable.map((r) => ({
              a: r.capability,
              b: r.reason,
            }))}
          />
        </Box>
      )}

      {section === 5 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Errores comunes
          </Typography>
          <Typography component="ul" sx={{ pl: 3 }}>
            <li><strong>401</strong> — Token JWT expirado o PAT no reconocido (authorizer).</li>
            <li><strong>403</strong> — Permisos insuficientes para la operación.</li>
            <li><strong>GraphQL validation</strong> — Revisá campos requeridos en el schema.</li>
            <li><strong>409 DUPLICATE_CONTENT</strong> — Mismo MD5 ya procesado; usá el document_id existente.</li>
            <li><strong>500</strong> — Error interno; revisá CloudWatch de la lambda.</li>
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
